import {
    Component,
    OnInit,
    Injector,
    ViewEncapsulation,
    ViewChild,
} from "@angular/core";
import { AbpSessionService } from "@abp/session/abp-session.service";
import { AppSessionService } from "@shared/common/session/app-session.service";
import { AbpMultiTenancyService } from "@abp/multi-tenancy/abp-multi-tenancy.service";
import {
    ProfileServiceProxy,
    UserLinkServiceProxy,
    LinkedUserDto,
    ChangeUserLanguageDto,
    TenantLoginInfoDto,
    GetCurrentLoginInformationsOutput,
    SessionServiceProxy,
} from "@shared/service-proxies/service-proxies";
import { AppComponentBase } from "@shared/common/app-component-base";

import { LoginAttemptsModalComponent } from "./login-attempts-modal.component";
import { LinkedAccountsModalComponent } from "./linked-accounts-modal.component";
import { ChangeProfilePictureModalComponent } from "./profile/change-profile-picture-modal.component";
import { MySettingsModalComponent } from "./profile/my-settings-modal.component";
import { AppAuthService } from "@app/shared/common/auth/app-auth.service";
import { ImpersonationService } from "@app/admin/users/impersonation.service";
import { LinkedAccountService } from "@app/shared/layout/linked-account.service";
import { NotificationSettingsModalComponent } from "@app/shared/layout/notifications/notification-settings-modal.component";
import { UserNotificationHelper } from "@app/shared/layout/notifications/UserNotificationHelper";
import { AppConsts } from "@shared/AppConsts";
import { SubscriptionStartType, EditionPaymentType } from "@shared/AppEnums";

import * as _ from "lodash";
import * as moment from "moment";
import { CookieService } from "shared/utils/cookie.service";
import { CreateOrEditUserModalComponent } from "@app/admin/users/create-or-edit-user-modal.component";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";

@Component({
    templateUrl: "./header.component.html",
    selector: "header-bar",
    encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent extends AppComponentBase implements OnInit {
    @ViewChild("notificationSettingsModal")
    notificationSettingsModal: NotificationSettingsModalComponent;
    @ViewChild("loginAttemptsModal")
    loginAttemptsModal: LoginAttemptsModalComponent;
    @ViewChild("linkedAccountsModal")
    linkedAccountsModal: LinkedAccountsModalComponent;
    @ViewChild("changeProfilePictureModal")
    changeProfilePictureModal: ChangeProfilePictureModalComponent;
    @ViewChild("mySettingsModal") mySettingsModal: MySettingsModalComponent;

    @ViewChild("updateProfileModal")
    updateProfileModal: CreateOrEditUserModalComponent;

    languages: abp.localization.ILanguageInfo[];
    currentLanguage: abp.localization.ILanguageInfo;
    isImpersonatedLogin = false;
    isMultiTenancyEnabled: boolean = false;

    shownLoginNameTitle = "";
    shownLoginName = "";

    tenancyName = "";
    userName = "";

    profilePicture =
        AppConsts.appBaseUrl +
        "/assets/common/images/default-profile-picture.png";
    defaultLogo =
        AppConsts.appBaseUrl +
        "/assets/common/images/app-logo-on-" +
        this.ui.getAsideSkin() +
        ".png";
    recentlyLinkedUsers: LinkedUserDto[];
    unreadChatMessageCount = 0;

    remoteServiceBaseUrl: string = AppConsts.remoteServiceBaseUrl;

    chatConnected = false;

    tenant: TenantLoginInfoDto = new TenantLoginInfoDto();
    subscriptionStartType = SubscriptionStartType;
    editionPaymentType: typeof EditionPaymentType = EditionPaymentType;

    userLogged = this.appSession.user;

    showProgrammings: boolean = false;
    showEventWorks: boolean = false;

    constructor(
        injector: Injector,
        private route: ActivatedRoute,
        private _abpSessionService: AbpSessionService,
        private _abpMultiTenancyService: AbpMultiTenancyService,
        private _profileServiceProxy: ProfileServiceProxy,
        private _userLinkServiceProxy: UserLinkServiceProxy,
        private _authService: AppAuthService,
        private _impersonationService: ImpersonationService,
        private _linkedAccountService: LinkedAccountService,
        private _userNotificationHelper: UserNotificationHelper,
        private _sessionService: SessionServiceProxy,
        private _appSessionService: AppSessionService,
        private _cookieService: CookieService,
        private router: Router
    ) {
        super(injector);

        this.checkUrlScientifics(this.router.url);
        this.checkUrlScientificWorks(this.router.url);

        this.router.events.subscribe((e) => {
            if (e instanceof NavigationEnd) {
                this.checkUrlScientifics(e.url);
                this.checkUrlScientificWorks(e.url);
            }
        });
    }

    get multiTenancySideIsTenant(): boolean {
        return this._abpSessionService.tenantId > 0;
    }

    ngOnInit() {
        this.isMultiTenancyEnabled = this._abpMultiTenancyService.isEnabled;
        this._userNotificationHelper.settingsModal =
            this.notificationSettingsModal;

        this.languages = _.filter(
            this.localization.languages,
            (l) => (<any>l).isDisabled === false
        );

        if (this.route.snapshot.queryParamMap.get("idioma")) {
            let langName = this.route.snapshot.queryParamMap.get("idioma");
            let lang = this.languages.filter((x) => x.name == langName)[0];

            if (this.userLogged) this.changeLanguage(langName);
            else if (lang) this.changeLanguageLocal(lang);
            else this.currentLanguage = this.localization.currentLanguage;
        } else {
            this.currentLanguage = this.localization.currentLanguage;
        }
        this.isImpersonatedLogin =
            this._abpSessionService.impersonatorUserId > 0;

        this.shownLoginNameTitle = this.isImpersonatedLogin
            ? this.l("YouCanBackToYourAccount")
            : "";

        if (this.userLogged) {
            this.getCurrentLoginInformations();
            this.getProfilePicture();
            this.getRecentlyLinkedUsers();
            this.registerToEvents();
        }
    }

    checkUrlScientifics(url: string) {
        if (url.includes("scientific") && !url.includes("scientificWork")) {
            this.showProgrammings = true;
        } else {
            this.showProgrammings = false;
        }
    }

    checkUrlScientificWorks(url: string) {
        if (
            url.includes("scientificWork") &&
            (this.permission.isGranted(
                "Pages.ScientificWorks.EventWorks.Management"
            ) ||
                this.permission.isGranted(
                    "Pages.ScientificWorks.EventWorks.Timeline"
                ) ||
                this.permission.isGranted("Pages.ScientificWorks.EPosters"))
        ) {
            this.showEventWorks = true;
        } else {
            this.showEventWorks = false;
        }
    }

    registerToEvents() {
        abp.event.on("profilePictureChanged", () => {
            this.getProfilePicture();
        });

        abp.event.on("app.chat.unreadMessageCountChanged", (messageCount) => {
            this.unreadChatMessageCount = messageCount;
        });

        abp.event.on("app.chat.connected", () => {
            this.chatConnected = true;
        });
    }

    changeLanguage(languageName: string): void {
        const input = new ChangeUserLanguageDto();
        input.languageName = languageName;
        let self = this;
        if (this.userLogged) {
            this._profileServiceProxy.changeLanguage(input).subscribe(() => {
                self._cookieService.setCookieValue(
                    "Abp.Localization.CultureName",
                    languageName,
                    new Date(new Date().getTime() + 5 * 365 * 86400000), //5 year
                    abp.appPath
                );

                this.router.navigate([], {
                    queryParams: { idioma: null },
                    queryParamsHandling: "merge",
                });
                setTimeout(function () {
                    window.location.reload();
                }, 500);
            });
        } else {
            let lang = this.languages.filter((x) => x.name == languageName)[0];
            this.changeLanguageLocal(lang);
        }
    }

    changeLanguageLocal(language: abp.localization.ILanguageInfo) {
        this._cookieService.setCookieValue(
            "Abp.Localization.CultureName",
            language.name,
            new Date(new Date().getTime() + 5 * 365 * 86400000), // 5 year
            abp.appPath
        );
        this.router.navigate([], {
            queryParams: { idioma: null },
            queryParamsHandling: "merge",
        });
        setTimeout(function () {
            window.location.reload();
        }, 500);
    }

    getCurrentLoginInformations(): void {
        this.shownLoginName = this.appSession.getShownLoginName();
        this.tenancyName = this.appSession.tenancyName;
        this.userName = this.appSession.user.userName;

        this._sessionService
            .getCurrentLoginInformations()
            .subscribe((result: GetCurrentLoginInformationsOutput) => {
                this.tenant = result.tenant;
            });
    }

    getShownUserName(linkedUser: LinkedUserDto): string {
        if (!this._abpMultiTenancyService.isEnabled) {
            return linkedUser.username;
        }

        return (
            (linkedUser.tenantId ? linkedUser.tenancyName : ".") +
            "\\" +
            linkedUser.username
        );
    }

    getProfilePicture(): void {
        this._profileServiceProxy.getProfilePicture().subscribe((result) => {
            if (result && result.profilePicture) {
                this.profilePicture =
                    "data:image/jpeg;base64," + result.profilePicture;
            }
        });
    }

    getRecentlyLinkedUsers(): void {
        this._userLinkServiceProxy
            .getRecentlyUsedLinkedUsers()
            .subscribe((result) => {
                this.recentlyLinkedUsers = result.items;
            });
    }

    showLoginAttempts(): void {
        this.loginAttemptsModal.show();
    }

    showLinkedAccounts(): void {
        this.linkedAccountsModal.show();
    }

    changePassword(): void {
        this.gotoChangePassword();
    }

    changeProfilePicture(): void {
        this.changeProfilePictureModal.show();
    }

    changeProfile(): void {
        this.updateProfileModal.show(abp.session.userId);
    }

    changeMySettings(): void {
        this.mySettingsModal.show();
    }

    logout(): void {
        this._authService.logout();
    }

    onMySettingsModalSaved(): void {
        this.shownLoginName = this.appSession.getShownLoginName();
    }

    backToMyAccount(): void {
        this._impersonationService.backToImpersonator();
    }

    switchToLinkedUser(linkedUser: LinkedUserDto): void {
        this._linkedAccountService.switchToAccount(
            linkedUser.id,
            linkedUser.tenantId
        );
    }

    get chatEnabled(): boolean {
        return this.appSession.application ? (
            this.appSession.application.features["SignalR"] &&
            (!this._abpSessionService.tenantId ||
                this.feature.isEnabled("App.ChatFeature"))
        ) : false;
    }

    subscriptionStatusBarVisible(): boolean {
        return (
            this._appSessionService.tenantId > 0 &&
            (this._appSessionService.tenant.isInTrialPeriod ||
                this.subscriptionIsExpiringSoon())
        );
    }

    subscriptionIsExpiringSoon(): boolean {
        if (this._appSessionService.tenant.subscriptionEndDateUtc) {
            return (
                moment()
                    .utc()
                    .add(AppConsts.subscriptionExpireNootifyDayCount, "days") >=
                moment(this._appSessionService.tenant.subscriptionEndDateUtc)
            );
        }

        return false;
    }

    getSubscriptionExpiringDayCount(): number {
        if (!this._appSessionService.tenant.subscriptionEndDateUtc) {
            return 0;
        }

        return Math.round(
            moment(this._appSessionService.tenant.subscriptionEndDateUtc).diff(
                moment().utc(),
                "days",
                true
            )
        );
    }

    getTrialSubscriptionNotification(): string {
        return abp.utils.formatString(
            this.l("TrialSubscriptionNotification"),
            "<strong>" +
                this._appSessionService.tenant.edition.displayName +
                "</strong>",
            "<a href='/account/buy?editionId=" +
                this._appSessionService.tenant.edition.id +
                "&editionPaymentType=" +
                this.editionPaymentType.BuyNow +
                "'>" +
                this.l("ClickHere") +
                "</a>"
        );
    }

    getExpireNotification(localizationKey: string): string {
        return abp.utils.formatString(
            this.l(localizationKey),
            this.getSubscriptionExpiringDayCount()
        );
    }

    isCongressLayout(): boolean {
        return this.router.url.indexOf("/congress/") >= 0;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/layout/header.component.ts